import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, XCircle, RefreshCw, Download } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Alert from '../../components/ui/Alert'
import Loading from '../../components/ui/Loading'
import SignatureCanvas from '../../components/SignatureCanvas'
import { dumperHourmeterService, type RemisionData } from '../../services/dumperApi'
import { useAuth } from '../../contexts/AuthContext'
import RemisionVolquetaPreview from '../RegisterDumperTrip/RemisionVolquetaPreview'
import {
  DUMPER_STATUS_COLOR,
  DUMPER_STATUS_LABEL,
  type DumperHourmeter,
} from '../../types/dumper'

type Action = 'idle' | 'sign-in-person' | 'sign-otp' | 'reject'

const DumperTripDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { operator } = useAuth()
  const tripId = Number(id)

  const [trip, setTrip] = useState<DumperHourmeter | null>(null)
  const [remisionData, setRemisionData] = useState<RemisionData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [action, setAction] = useState<Action>('idle')
  const [code, setCode] = useState('')
  const [observation, setObservation] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [info, setInfo] = useState<string | null>(null)
  const sigRef = useRef<{ clear: () => void; toDataURL: () => string } | null>(null)

  useEffect(() => {
    let m = true
    setLoading(true)
    Promise.all([
      dumperHourmeterService.findOne(tripId),
      dumperHourmeterService.getRemisionData(tripId).catch(() => null),
    ])
      .then(([t, r]) => {
        if (!m) return
        setTrip(t)
        setRemisionData(r)
      })
      .catch((e) => m && setError(e?.response?.data?.message || 'Error cargando viaje'))
      .finally(() => m && setLoading(false))
    return () => { m = false }
  }, [tripId])

  const reload = async () => {
    try {
      const [t, r] = await Promise.all([
        dumperHourmeterService.findOne(tripId),
        dumperHourmeterService.getRemisionData(tripId).catch(() => null),
      ])
      setTrip(t)
      setRemisionData(r)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error recargando viaje')
    }
  }

  const isResident = operator?.role === 'resident'
  const canSign = isResident && trip?.status === 'PENDIENTE'
  // Driver puede facilitar la firma del residente entregando el dispositivo (RS-1338).
  // Residente firma OTP con su código o in-person dibujando.
  const canFacilitateSign = !isResident && trip?.status === 'PENDIENTE' && trip?.residentId

  // Si residente + viaje PENDIENTE, abrir el canvas de firma directamente
  useEffect(() => {
    if (canSign && action === 'idle') {
      setAction('sign-in-person')
    }
  }, [canSign])
  const canReject = isResident && trip?.status === 'PENDIENTE'
  const canResend = false  // RS-1338: ya no usamos OTP, reenvío no aplica
  const canEdit = !isResident && trip && ['PENDIENTE', 'RECHAZADO', 'FIRMADO', 'REGISTRADO'].includes(trip.status)
  const canDownloadActa = trip && ['FIRMADO', 'REGISTRADO'].includes(trip.status)
  const [downloadingActa, setDownloadingActa] = useState(false)

  const handleDownloadActa = async () => {
    if (!trip) return
    setDownloadingActa(true)
    setError(null)
    try {
      await dumperHourmeterService.downloadActa(trip.id)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo generar el PDF')
    } finally {
      setDownloadingActa(false)
    }
  }

  const handleSignInPerson = async () => {
    // Si soy residente firmo con mi ID; si soy driver facilitando, uso el residentId del trip
    const residentId = isResident ? operator?.id : trip?.residentId
    if (!residentId) return
    setSubmitting(true)
    setError(null)
    try {
      await dumperHourmeterService.signInPerson(tripId, {
        residentId,
        observation: observation || undefined,
      })
      setInfo('Viaje firmado correctamente')
      setAction('idle')
      await reload()
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo firmar')
    } finally {
      setSubmitting(false)
    }
  }

  const handleReject = async () => {
    if (!operator?.id || !rejectReason) return
    setSubmitting(true)
    setError(null)
    try {
      await dumperHourmeterService.reject(tripId, {
        residentId: operator.id,
        rejectReason,
        code: trip?.signMode === 'OTP' ? code : undefined,
      })
      setInfo('Viaje rechazado')
      setAction('idle')
      setRejectReason('')
      setCode('')
      await reload()
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo rechazar')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await dumperHourmeterService.resend(tripId)
      setInfo('Viaje reenviado para firma')
      await reload()
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo reenviar')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" text="Cargando viaje…" />
      </div>
    )
  }
  if (!trip) {
    return (
      <div className="p-6">
        <Alert variant="error">{error || 'Viaje no encontrado'}</Alert>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-retri-blue px-3 py-3 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-white font-semibold">Viaje #{trip.id}</h1>
        <span
          className={`ml-auto text-xs font-medium px-2 py-1 rounded ${DUMPER_STATUS_COLOR[trip.status]}`}
        >
          {DUMPER_STATUS_LABEL[trip.status]}
        </span>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        {info && <Alert variant="success">{info}</Alert>}
        {error && <Alert variant="error">{error}</Alert>}

        {/* Recibo visual — reusa el componente del form de registro */}
        {remisionData && (
          <RemisionVolquetaPreview
            form={{
              realM3: remisionData.realM3,
              tripCount: String(remisionData.tripCount),
              distanceKm: remisionData.distanciaKm,
              materialType: remisionData.tipoMaterial,
              remisionNumber: remisionData.numero,
              photoUrl: remisionData.fotoAdjunta || '',
              residentObservation: remisionData.observaciones || '',
              loadingZone: remisionData.sitioCargue,
              unloadingZone: remisionData.sitioDescargue,
            }}
            ctx={{
              licensePlate: remisionData.placa || null,
              driverName: remisionData.nombreAutorizado,
              dealId: trip.dealId || null,
              suggestedRemision: remisionData.numero,
              buildingName: remisionData.obra || null,
              driverSignatureUrl: remisionData.firmaTransportador || null,
            }}
            materialAssetSelected={
              remisionData.razonSocial || remisionData.tipoMaterial
                ? {
                    supplierCompanyName: remisionData.razonSocial,
                    materialType: remisionData.tipoMaterial,
                  }
                : null
            }
            currentDate={`${remisionData.dia}/${remisionData.mes}/${remisionData.anio}`}
            currentTime={remisionData.horaRegistro}
          />
        )}

        {(canSign || canReject || canResend || canEdit || canDownloadActa || canFacilitateSign) && (
          <div className="flex flex-wrap gap-2">
            {canDownloadActa && (
              <Button variant="outline" onClick={handleDownloadActa} disabled={downloadingActa}>
                <Download className="w-4 h-4 mr-2" />
                {downloadingActa ? 'Generando…' : 'Descargar acta PDF'}
              </Button>
            )}
            {canSign && (
              <Button onClick={() => setAction('sign-in-person')}>
                <CheckCircle2 className="w-4 h-4 mr-2" /> Firmar viaje
              </Button>
            )}
            {canFacilitateSign && (
              <Button onClick={() => setAction('sign-in-person')}>
                <CheckCircle2 className="w-4 h-4 mr-2" /> Firma residente
              </Button>
            )}
            {canReject && (
              <Button variant="danger" onClick={() => setAction('reject')}>
                <XCircle className="w-4 h-4 mr-2" /> Rechazar
              </Button>
            )}
            {canResend && (
              <Button onClick={handleResend} disabled={submitting}>
                <RefreshCw className="w-4 h-4 mr-2" /> Reenviar
              </Button>
            )}
            {canEdit && (
              <Button
                variant="secondary"
                onClick={() => navigate(`/dumper-hourmeters/register?editId=${trip.id}`)}
              >
                Editar
              </Button>
            )}
          </div>
        )}

        {action === 'sign-in-person' && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <h3 className="font-semibold text-gray-900">Firma del residente</h3>
              {!isResident && (
                <Alert variant="info">
                  Entrega el dispositivo al residente para que firme aquí.
                </Alert>
              )}
              <SignatureCanvas ref={sigRef as any} className="w-full" />
              <Input
                label="Observación (opcional)"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
              />
              <div className="flex gap-2">
                <Button onClick={handleSignInPerson} disabled={submitting}>Confirmar firma</Button>
                <Button variant="ghost" onClick={() => setAction('idle')}>Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {action === 'reject' && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <h3 className="font-semibold text-gray-900">Rechazar viaje</h3>
              <Input
                label="Motivo del rechazo"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  onClick={handleReject}
                  disabled={submitting || !rejectReason}
                >
                  Confirmar rechazo
                </Button>
                <Button variant="ghost" onClick={() => setAction('idle')}>Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  )
}

export default DumperTripDetail
